/**
 * ConversationDetailPage.tsx — conversation detail view + controls (CONV8).
 *
 * Reached from the CONV7 list via `#conversations/<thread_id>`. Renders the
 * full ConversationDetail with masked phone (PII-never-raw), a lead deep-link
 * into the Leads panel (`#leads/<lead_id>` — the SPA's hash route convention),
 * live pause/resume controls, and gated sections:
 *
 *   • Reset lead  — the combined CONV11c action (ADR-079); NOT gated via
 *                    `detail.reset_availability` anymore (see below)
 *   • Requeue     — gated via `detail.requeue_availability` (DISTINCT gate)
 *
 * Requeue renders its own disabled-button + gate-reason markup (ADR-058
 * Addendum C).
 *
 * One reader (Aron's 2026-09-19 ruling): the Overview transcript pane and the
 * "Auditoria" tab are gone — MessageLedgerTab ("Todas as mensagens") is now
 * the single place to read a conversation's messages (and, via its "Mostrar
 * decisões" switch, its decision ledger). `detail.transcript` stays on the
 * ConversationDetail contract untouched; this page just no longer renders it.
 *
 * Reset (CONV11c, ADR-079 D4/D5) — `detail.reset_availability` described the
 * OLD gated per-thread reset control (CONV4/CONV8), hardcoded unavailable
 * behind the durable checkpointer. CONV11c REWIRES that control to the new
 * combined "reset lead" BFF action (`POST .../reset-lead`), which is live
 * regardless of `reset_availability` — the SPA no longer consults that field
 * for this control (AC6: rewired, not left gated in place). The reset button
 * is disabled only when there is no linked lead (`lead_link == null`), since
 * the combined action requires a `lead_id`.
 *
 * NOTE — deep-link discrepancy: the contract's `lead_link.deep_link` carries
 * "/leads?lead_id=<uuid>" (query-string form). The SPA uses hash routing
 * (`#leads/<uuid>`) as established in CONV7's list and LeadDetailPage's back
 * link. We construct the hash-route link from `lead_link.lead_id` and ignore
 * `lead_link.deep_link`. Filed as a contract discrepancy below.
 *
 * Control UX:
 *   - pause / resume: live, button disabled while submitting (optimistic lock).
 *     On success → refetch detail. On 409 → "conflict" banner + refresh button.
 *     On 422 / 404 / 502 → error message with BFF `detail` if available.
 *   - requeue: rendered DISABLED (no live route); gate reason shown as
 *     accessible help text (aria-describedby).
 *
 * WCAG 2.1 AA: disabled controls have `disabled` attribute + visible
 * `aria-describedby` text explaining why; phone never raw; colour-coded badges
 * always paired with text labels (CONV7 convention).
 */

import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import { ApiError, UnauthorizedError } from '../../api/client';
import type { ConversationDetail, ControlRequest } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { Button } from '../../components/Button';
import { formatDateTime } from '../leads/formatDateTime';
import { ConsentBadge, DeliveryBadge, FunnelStageBadge, PausedBadge } from './ConversationBadges';
import { MessageLedgerTab } from './MessageLedgerTab';
import { ResetLeadConfirm } from './ResetLeadConfirm';
import { TracesTab } from './TracesTab';
import { useConversationDetail } from './useConversationDetail';
import floating from '../../lib/floating';

export interface ConversationDetailPageProps {
  apiClient: ApiClient;
  threadId: string;
}

type ControlPhase = 'idle' | 'submitting' | 'conflict' | 'error';

const BackLink = ({ label }: { label: string }) => (
  <a
    href="#conversations"
    data-testid="conv-detail-back"
    className="inline-flex w-fit items-center gap-1 rounded text-meta max-md:min-h-11 text-gold underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-outline-focus"
  >
    <span aria-hidden="true">←</span> {label}
  </a>
);

// ---------------------------------------------------------------------------
// Controls section (pause / resume / requeue)
// ---------------------------------------------------------------------------

interface ControlsSectionProps {
  detail: ConversationDetail;
  apiClient: ApiClient;
  threadId: string;
  onRefetch: () => void;
}

const ControlsSection = ({ detail, apiClient, threadId, onRefetch }: ControlsSectionProps) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<ControlPhase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requeueDescId = useId();

  const submit = useCallback(
    async (action: 'pause' | 'resume') => {
      setPhase('submitting');
      setErrorMessage(null);
      const body: ControlRequest = { action };
      try {
        await apiClient.controlConversation(threadId, body);
        setPhase('idle');
        onRefetch();
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          // Let the parent detect the unauth on refetch; reset to idle so the
          // buttons don't stay frozen.
          setPhase('idle');
          return;
        }
        if (error instanceof ApiError && error.status === 409) {
          setPhase('conflict');
          return;
        }
        // 422 / 404 / 502 / transport
        const bffDetail = error instanceof ApiError ? error.detail : null;
        setErrorMessage(
          bffDetail != null
            ? t('conversations.detail.controlDetail', { detail: bffDetail })
            : t('conversations.detail.controlError'),
        );
        setPhase('error');
      }
    },
    [apiClient, threadId, onRefetch, t],
  );

  const busy = phase === 'submitting';

  return (
    <section aria-labelledby="conv-controls-heading" className="flex flex-col gap-3">
      <h2
        id="conv-controls-heading"
        className="text-meta font-semibold uppercase tracking-wide text-ink-3"
      >
        {t('conversations.detail.controls')}
      </h2>

      {phase === 'conflict' ? (
        <div
          role="alert"
          data-testid="conv-detail-conflict"
          className="rounded-lg bg-status-degraded/15 px-4 py-2 text-meta text-status-degraded ring-1 ring-inset ring-status-degraded/40"
        >
          <p>{t('conversations.detail.conflict')}</p>
          <Button
            size="sm"
            data-testid="conv-detail-refresh"
            onClick={() => {
              setPhase('idle');
              onRefetch();
            }}
            className="mt-2 min-h-11 md:min-h-0"
          >
            {t('conversations.detail.refresh')}
          </Button>
        </div>
      ) : null}

      {phase === 'error' && errorMessage != null ? (
        <p
          role="alert"
          data-testid="conv-detail-control-error"
          className="rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40"
        >
          {errorMessage}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {detail.available_controls.includes('pause') ? (
          <Button
            data-testid="conv-detail-pause"
            onClick={() => floating(submit('pause'))}
            disabled={busy}
            aria-busy={busy}
            className="min-h-11 md:min-h-0"
          >
            {busy ? t('conversations.detail.pausing') : t('conversations.detail.pause')}
          </Button>
        ) : null}

        {detail.available_controls.includes('resume') ? (
          <Button
            data-testid="conv-detail-resume"
            onClick={() => floating(submit('resume'))}
            disabled={busy}
            aria-busy={busy}
            className="min-h-11 md:min-h-0"
          >
            {busy ? t('conversations.detail.resuming') : t('conversations.detail.resume')}
          </Button>
        ) : null}

        {/* Requeue — GATED (ADR-058 Addendum C). No live route. Disabled + gate
            reason visible via aria-describedby for WCAG 2.1 AA. Uses its OWN
            `requeue_availability` source, distinct from the transcript/reset gate. */}
        <div className="flex flex-col gap-1">
          <Button
            data-testid="conv-detail-requeue"
            disabled
            aria-disabled="true"
            aria-describedby={requeueDescId}
            className="min-h-11 cursor-not-allowed opacity-50 md:min-h-0"
          >
            {t('conversations.detail.requeue')}
          </Button>
          <div
            id={requeueDescId}
            data-testid="conv-detail-requeue-gate"
            className="flex flex-col gap-0.5 px-1 text-meta text-ink-3"
          >
            <span data-testid="conv-detail-requeue-unavailable">
              {t('conversations.gated.unavailable')}
            </span>
            {/* Raw gate rationale (outbox_events / ADR refs / Service Bus) is
                operator-noise — tuck it behind a collapsed disclosure so the
                terse "not yet available" line leads. */}
            <details data-testid="conv-detail-requeue-details" className="text-meta text-ink-3">
              <summary className="cursor-pointer text-ink-2 marker:text-ink-3">
                {t('conversations.gated.details')}
              </summary>
              <div className="mt-1 flex flex-col gap-0.5">
                <span data-testid="conv-detail-requeue-reason">
                  {t('conversations.gated.reason', { reason: detail.requeue_availability.reason })}
                </span>
                <span data-testid="conv-detail-requeue-unblocked-by">
                  {t('conversations.gated.unblockedBy', {
                    dep: detail.requeue_availability.unblocked_by,
                  })}
                </span>
              </div>
            </details>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Reset lead — the combined action (CONV11c, ADR-079). Sole reset trigger
// (AC1/AC6): rewires the old CONV4/CONV8 gated control, not a second one
// beside it. Requires a linked lead; disabled with a visible reason when
// `lead_link` is null.
// ---------------------------------------------------------------------------

interface ResetLeadSectionProps {
  detail: ConversationDetail;
  apiClient: ApiClient;
  threadId: string;
  onRefetch: () => void;
}

const ResetLeadSection = ({ detail, apiClient, threadId, onRefetch }: ResetLeadSectionProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const leadId = detail.lead_link?.lead_id ?? null;
  const noLeadDescId = useId();

  return (
    <section
      aria-labelledby="conv-reset-lead-heading"
      data-testid="conv-detail-danger-zone"
      className="flex flex-col gap-3 border-t border-status-down/30 pt-4"
    >
      <h2
        id="conv-reset-lead-heading"
        className="text-meta font-semibold uppercase tracking-wide text-status-down"
      >
        {t('conversations.detail.resetLead.heading')}
      </h2>

      {open && leadId != null ? (
        <ResetLeadConfirm
          apiClient={apiClient}
          threadId={threadId}
          leadId={leadId}
          onCancel={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            onRefetch();
          }}
        />
      ) : (
        <div className="flex flex-col gap-1">
          <Button
            variant="danger"
            data-testid="conv-detail-reset-lead-action"
            disabled={leadId == null}
            aria-disabled={leadId == null}
            aria-describedby={leadId == null ? noLeadDescId : undefined}
            onClick={() => setOpen(true)}
            className="min-h-11 w-fit disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0"
          >
            {t('conversations.detail.resetLead.trigger')}
          </Button>
          {leadId == null ? (
            <span
              id={noLeadDescId}
              data-testid="conv-reset-lead-no-lead"
              className="px-1 text-meta text-ink-3"
            >
              {t('conversations.detail.resetLead.noLead')}
            </span>
          ) : null}
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type DetailTab = 'messages' | 'traces';

const PANEL_KEY = 'tcn.conv.panel';
const PANEL_ID = 'conv-detail-panel';

// Inline panel state at lg+ persists ('0' = hidden; default shown). The
// overlay below lg is separate session state and always starts closed.
const readPanelPref = (): boolean => {
  try {
    return window.localStorage.getItem(PANEL_KEY) !== '0';
  } catch {
    return true;
  }
};

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col">
    <dt className="text-meta text-ink-3">{label}</dt>
    <dd className="break-words text-body text-ink-1">{children}</dd>
  </div>
);

export const ConversationDetailPage = ({ apiClient, threadId }: ConversationDetailPageProps) => {
  const { t, i18n } = useTranslation();
  const { detail, phase, refetch } = useConversationDetail(apiClient, threadId);
  const [activeTab, setActiveTab] = useState<DetailTab>('messages');
  const [panelInline, setPanelInline] = useState(readPanelPref);
  const [panelOverlay, setPanelOverlay] = useState(false);
  // Bumped on a successful reset so the ledger tab REMOUNTS (both its hooks
  // refetch on mount) instead of re-rendering a pre-reset message list.
  const [resetNonce, setResetNonce] = useState(0);

  const toggleInline = () => {
    const next = !panelInline;
    setPanelInline(next);
    try {
      window.localStorage.setItem(PANEL_KEY, next ? '1' : '0');
    } catch {
      /* storage unavailable: the preference just won't persist */
    }
  };

  // Escape closes the overlay whenever it is open (any focus), and the inline
  // panel when focus is inside it (handled on the <aside>).
  useEffect(() => {
    if (!panelOverlay) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPanelOverlay(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [panelOverlay]);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  if (phase === 'loading' && detail == null) {
    return (
      <section className="flex flex-col gap-4">
        <BackLink label={t('conversations.detail.back')} />
        <p role="status" data-testid="conv-detail-loading" className="text-body text-ink-2">
          {t('conversations.detail.loading')}
        </p>
      </section>
    );
  }

  if (phase === 'notFound') {
    return (
      <section className="flex flex-col gap-4">
        <BackLink label={t('conversations.detail.back')} />
        <p role="alert" data-testid="conv-detail-notfound" className="text-body text-ink-2">
          {t('conversations.detail.notFound')}
        </p>
      </section>
    );
  }

  if (phase === 'error' && detail == null) {
    return (
      <section className="flex flex-col gap-4">
        <BackLink label={t('conversations.detail.back')} />
        <div className="flex flex-col items-start gap-3" data-testid="conv-detail-error">
          <p role="alert" className="text-body text-status-down">
            {t('conversations.detail.errorBody')}
          </p>
          <Button data-testid="conv-detail-retry" onClick={refetch} className="min-h-11 md:min-h-0">
            {t('conversations.detail.retry')}
          </Button>
        </div>
      </section>
    );
  }

  if (detail == null) {
    // Defensive: ready phase always has a detail.
    return null;
  }

  const tabClass = (tab: DetailTab) =>
    `min-h-11 rounded-md px-3 py-1.5 text-body font-medium focus:outline-none focus:ring-2 focus:ring-outline-focus md:min-h-0 ${
      activeTab === tab ? 'bg-surface-1 text-ink-1' : 'text-ink-3 hover:text-ink-1'
    }`;

  return (
    <div className="flex items-start gap-4">
      <section
        aria-labelledby="conv-detail-heading"
        data-testid="conv-detail-page"
        className="flex min-w-0 flex-1 flex-col gap-4"
      >
        <header className="flex flex-col gap-1">
          <BackLink label={t('conversations.detail.back')} />
          <div className="flex items-start justify-between gap-3">
            <h1
              id="conv-detail-heading"
              data-testid="conv-detail-phone"
              className="text-h1 text-ink-1"
            >
              {/* phone_masked is already masked by the BFF — display verbatim (Q3). */}
              {detail.phone_masked}
            </h1>
            <Button
              data-testid="conv-detail-panel-toggle"
              aria-expanded={panelInline}
              aria-controls={PANEL_ID}
              onClick={toggleInline}
              className="hidden shrink-0 lg:inline-flex"
            >
              {panelInline
                ? t('conversations.detail.panel.hide')
                : t('conversations.detail.panel.show')}
            </Button>
            <Button
              data-testid="conv-detail-panel-toggle-overlay"
              aria-expanded={panelOverlay}
              aria-controls={PANEL_ID}
              onClick={() => setPanelOverlay(true)}
              className="min-h-11 shrink-0 lg:hidden"
            >
              {t('conversations.detail.panel.show')}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {detail.funnel_stage ? <FunnelStageBadge stage={detail.funnel_stage} /> : '—'}
            <DeliveryBadge status={detail.delivery_status} />
            <ConsentBadge state={detail.consent_state} />
            <PausedBadge paused={detail.is_paused} />
          </div>
        </header>

        {/* Segmented control — Messages / Traces. Plain buttons with
            aria-pressed, not the full ARIA tabs pattern. */}
        <div
          role="group"
          aria-label={t('conversations.detail.tabsLabel')}
          className="flex w-fit gap-1 rounded-lg bg-surface-2 p-1"
        >
          <button
            type="button"
            data-testid="conv-detail-tab-messages"
            aria-pressed={activeTab === 'messages'}
            onClick={() => setActiveTab('messages')}
            className={tabClass('messages')}
          >
            {t('conversations.detail.tabs.messages')}
          </button>
          <button
            type="button"
            data-testid="conv-detail-tab-traces"
            aria-pressed={activeTab === 'traces'}
            onClick={() => setActiveTab('traces')}
            className={tabClass('traces')}
          >
            {t('conversations.detail.tabs.traces')}
          </button>
        </div>

        {activeTab === 'messages' ? (
          <section data-testid="conv-detail-messages-pane">
            <MessageLedgerTab key={resetNonce} apiClient={apiClient} threadId={threadId} />
          </section>
        ) : (
          <section data-testid="conv-detail-traces-pane">
            <TracesTab apiClient={apiClient} threadId={threadId} />
          </section>
        )}
      </section>

      {/* Right panel: overlay below lg (closed by default), inline + hideable at lg+. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Escape-to-dismiss keyboard handler on a landmark */}
      <aside
        id={PANEL_ID}
        aria-label={t('conversations.detail.panel.label')}
        data-testid="conv-detail-panel"
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || panelOverlay) return;
          if (panelInline) toggleInline();
        }}
        className={`${
          panelOverlay ? 'fixed inset-y-0 right-0 z-30 flex' : 'hidden'
        } w-80 max-w-full flex-col gap-5 overflow-y-auto border-l border-ink-3/20 bg-surface-1 p-4 lg:static lg:z-auto lg:w-72 lg:shrink-0 lg:overflow-visible lg:bg-transparent lg:p-0 lg:pl-4 ${
          panelInline ? 'lg:flex' : 'lg:hidden'
        }`}
      >
        <Button
          data-testid="conv-detail-panel-close"
          onClick={() => setPanelOverlay(false)}
          className="min-h-11 w-fit lg:hidden"
        >
          {t('conversations.detail.panel.close')}
        </Button>

        {/* Facts */}
        <dl className="flex flex-col gap-2" data-testid="conv-detail-metadata">
          <Fact label={t('conversations.detail.channel')}>{detail.channel}</Fact>
          <Fact label={t('conversations.detail.waba')}>
            {detail.waba_number ?? t('conversations.detail.notAvailable')}
          </Fact>
          <Fact label={t('conversations.detail.scriptStep')}>
            {detail.script_step ?? t('conversations.detail.notAvailable')}
          </Fact>
          <Fact label={t('conversations.detail.inboundMessages')}>
            {detail.inbound_message_count}
          </Fact>
          <Fact label={t('conversations.detail.lastActivity')}>
            {formatDateTime(detail.last_activity_at, i18n.language)}
          </Fact>
          {detail.started_at != null ? (
            <Fact label={t('conversations.detail.startedAt')}>
              {formatDateTime(detail.started_at, i18n.language)}
            </Fact>
          ) : null}
          <Fact label={t('conversations.detail.lead')}>
            {detail.lead_link != null ? (
              /* Deep-link discrepancy: the contract's `lead_link.deep_link` is
               * "/leads?lead_id=<uuid>" (query-string form). The SPA uses hash
               * routing `#leads/<uuid>`. We follow the app convention and
               * construct from lead_link.lead_id. See CONV8 report notes. */
              <a
                href={`#leads/${encodeURIComponent(detail.lead_link.lead_id)}`}
                data-testid="conv-detail-lead-link"
                aria-label={t('conversations.detail.viewLead')}
                className="inline-flex min-h-11 items-center rounded text-gold underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-outline-focus md:min-h-0"
              >
                {t('conversations.detail.viewLead')}
              </a>
            ) : (
              <span data-testid="conv-detail-no-lead" className="text-ink-3">
                {t('conversations.detail.noLead')}
              </span>
            )}
          </Fact>
        </dl>

        {/* Controls — pause/resume (live) + requeue (gated) */}
        <ControlsSection
          detail={detail}
          apiClient={apiClient}
          threadId={threadId}
          onRefetch={refetch}
        />

        {/* Reset lead — the combined action (CONV11c), separated danger section. */}
        <ResetLeadSection
          detail={detail}
          apiClient={apiClient}
          threadId={threadId}
          onRefetch={() => {
            setResetNonce((n) => n + 1);
            refetch();
          }}
        />
      </aside>
    </div>
  );
};
